import create, { StateCreator } from 'zustand'

interface AbsoluteEmissionsTarget {
  reducePer: number
  year?: number | ''
}
export interface AbsoluteEmissionsSlice {
  absoluteEmissions: AbsoluteEmissionsTarget
  setAbsoluteEmissionsTarget: (target: AbsoluteEmissionsTarget) => void
  clearAbsoluteEmissionsTarget: () => void
  setAbsoluteEmissionsReductionPercent: (percent: number) => void
  setAbsoluteEmissionsYear: (year: number) => void
}

const createAbsoluteEmissionsSlice: StateCreator<AbsoluteEmissionsSlice> = set => ({
  absoluteEmissions: {
    reducePer: 20,
    year: '',
  },
  setAbsoluteEmissionsTarget: (absoluteEmissions: AbsoluteEmissionsTarget) =>
    set(state => ({ absoluteEmissions })),
  clearAbsoluteEmissionsTarget: () =>
    set(state => ({
      absoluteEmissions: {
        reducePer: 20,
        year: '',
      },
    })),
  setAbsoluteEmissionsReductionPercent: (percent: number) =>
    set(state => ({
      absoluteEmissions: {
        reducePer: percent,
        year: state.absoluteEmissions.year,
      },
    })),
  setAbsoluteEmissionsYear: (year: number) =>
    set(state => ({
      absoluteEmissions: {
        reducePer: state.absoluteEmissions.reducePer,
        year,
      },
    })),
})

interface RemovalsTarget {
  removeTonnes?: number
  year?: number | ''
}

export interface RemovalsSlice {
  removals?: RemovalsTarget
  createRemovalsTarget: (target?: RemovalsTarget) => void
  removeRemovalsTarget: () => void
  setRemovalsTonnes: (tonnes: number) => void
  setRemovalsYear: (year: number) => void
}

const createRemovalsSlice: StateCreator<RemovalsSlice> = set => ({
  removals: undefined,
  createRemovalsTarget: (target?: RemovalsTarget) =>
    set(state => ({ ...state, removals: target || { removeTonnes: undefined, year: '' } })),
  removeRemovalsTarget: () => set(state => ({ ...state, removals: undefined })),
  setRemovalsTonnes: (percent: number) =>
    set(state => ({
      removals: {
        removeTonnes: percent,
        year: state.removals?.year,
      },
    })),
  setRemovalsYear: (year: number) =>
    set(state => ({
      removals: {
        removeTonnes: state.removals?.removeTonnes,
        year,
      },
    })),
})

type CarbonIntensityTarget<C extends string> = {
  crop: C
  reduceTo?: number
  year?: number | ''
}

type CarbonIntensityValue = {
  soybeans: CarbonIntensityTarget<'soybeans'>
  corn: CarbonIntensityTarget<'corn'>
}

export interface CropCarbonIntensitySlice {
  carbonIntensity?: CarbonIntensityValue
  targetCrops: Array<'corn' | 'soybeans'>
  setTargetCrops: (vals: Array<'corn' | 'soybeans'>) => void
  setIntensityTargets: (targets: CarbonIntensityTarget<'soybeans' | 'corn'>[]) => void
  createEmptyIntensityTargets: () => void
  removeIntensityTargets: () => void
  setTargetIntensity: (crop: 'corn' | 'soybeans', reduceTo: number) => void
  setTargetYear: (year: number) => void
}

const createCropCarbonIntensitySlice: StateCreator<CropCarbonIntensitySlice> = set => ({
  carbonIntensity: undefined,
  setIntensityTargets: (targets: CarbonIntensityTarget<'soybeans' | 'corn'>[]) =>
    set(state => {
      // @ts-ignore i am indeed setting it as an invalid value to start
      const carbonIntensity: CarbonIntensityValue = {}
      // @ts-ignore this is correctly typed, but doesn't think it is
      targets.forEach(target => (carbonIntensity[target.crop] = target))
      return {
        ...state,
        carbonIntensity,
      }
    }),
  targetCrops: ['corn', 'soybeans'],
  setTargetCrops: (vals: Array<'corn' | 'soybeans'>) =>
    set(state => ({ ...state, targetCrops: vals })),
  removeIntensityTargets: () =>
    set(state => ({
      ...state,
      carbonIntensity: undefined,
    })),
  createEmptyIntensityTargets: () =>
    set(state => {
      if (!state.carbonIntensity) {
        return {
          ...state,
          carbonIntensity: {
            soybeans: {
              crop: 'soybeans',
              reduceTo: undefined,
              year: '',
            },
            corn: {
              crop: 'corn',
              reduceTo: undefined,
              year: '',
            },
          },
        }
      } else {
        return state
      }
    }),
  setTargetIntensity: (crop: 'corn' | 'soybeans', reduceTo: number) =>
    set(state => {
      if (!state.carbonIntensity) {
        state.createEmptyIntensityTargets()
      }

      const newCropValue = {
        crop,
        reduceTo,
        year: state.carbonIntensity?.[crop]?.year,
      }
      return {
        ...state,
        carbonIntensity: { ...state.carbonIntensity, [crop]: newCropValue } as CarbonIntensityValue,
      }
    }),
  setTargetYear: (year: number) =>
    set(state => {
      if (!state.carbonIntensity) {
        state.createEmptyIntensityTargets()
      }
      const carbonIntensity = state.carbonIntensity as CarbonIntensityValue
      const newCarbonIntensity: CarbonIntensityValue = {} as CarbonIntensityValue
      state.targetCrops.forEach(
        cropValue =>
          (newCarbonIntensity[cropValue as 'soybeans' | 'corn'] = {
            ...carbonIntensity[cropValue as 'soybeans' | 'corn'],
            // @ts-ignore I don't know why, but it thinks that 'year' should be 'never'
            year: year,
          })
      )
      return {
        ...state,
        carbonIntensity: newCarbonIntensity as CarbonIntensityValue,
      }
    }),
})

export const useInventoryTargetStore = create<
  AbsoluteEmissionsSlice & RemovalsSlice & CropCarbonIntensitySlice
>((...a) => ({
  ...createCropCarbonIntensitySlice(...a),
  ...createAbsoluteEmissionsSlice(...a),
  ...createRemovalsSlice(...a),
}))
